/**
 * HTTP client utility with error handling and type safety
 */

import { config } from '@/config';
import { notifyError, notifySuccess } from '@/utils/toast';
import type { ApiError, ApiResponse } from '@/types';

/**
 * HTTP methods supported by the client
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Request options for HTTP client
 */
export interface RequestOptions<TBody = unknown> {
    method?: HttpMethod;
    body?: TBody;
    headers?: Record<string, string>;
    signal?: AbortSignal;
    notifyOnSuccess?: boolean;
    successMessage?: string;
    successTitle?: string;
    notifyOnError?: boolean;
    errorTitle?: string;
    includeAuth?: boolean;
}

const MUTATION_METHODS: HttpMethod[] = ['POST', 'PUT', 'PATCH', 'DELETE'];

const RESOURCE_LABELS: Record<string, string> = {
    users: 'Cliente',
    notifications: 'Notificación',
    materials: 'Material',
    'historical-stock': 'Movimiento de stock',
    plagues: 'Plaga',
    treatments: 'Tratamiento',
    precautions: 'Precaución',
    'service-objects': 'Servicio',
    'service-types': 'Tipo de servicio',
    'service-requests': 'Solicitud',
    'work-materials': 'Material de orden',
    'work-orders': 'Orden de trabajo',
    budgets: 'Presupuesto',
    'budget-items': 'Línea de presupuesto',
    contacts: 'Contacto',
    cities: 'Ciudad',
    provinces: 'Provincia',
    reports: 'Informe',
    'audit-logs': 'Registro de auditoría',
};

const getResourceLabel = (endpoint: string): string => {
    const cleanPath = endpoint.split('?')[0].replace(/^\//, '');
    const firstSegment = cleanPath.split('/').filter(Boolean)[0] ?? '';
    return RESOURCE_LABELS[firstSegment] ?? 'Elemento';
};

const getDefaultSuccessTitle = (method: HttpMethod, endpoint: string): string => {
    const label = getResourceLabel(endpoint);

    switch (method) {
        case 'POST':
            return `${label} creado`;
        case 'PUT':
        case 'PATCH':
            return `${label} actualizado`;
        case 'DELETE':
            return `${label} eliminado`;
        default:
            return 'Operación completada';
    }
};

const getDefaultErrorTitle = (endpoint: string): string => {
    const label = getResourceLabel(endpoint);
    return `Error en ${label.toLowerCase()}`;
};

/**
 * Custom HTTP error class
 */
export class HttpError extends Error {
    constructor(
        public readonly status: number,
        public readonly statusText: string,
        public readonly apiError?: ApiError
    ) {
        super(apiError?.message || statusText);
        this.name = 'HttpError';
    }

    public get statusCode(): number {
        return this.status;
    }
}

/**
 * Network error class
 */
export class NetworkError extends Error {
    constructor(message: string = 'Network error occurred') {
        super(message);
        this.name = 'NetworkError';
    }
}

/**
 * Timeout error class
 */
export class TimeoutError extends Error {
    constructor(message: string = 'Request timeout') {
        super(message);
        this.name = 'TimeoutError';
    }
}

/**
 * Get authentication token from localStorage
 */
const getAuthToken = (): string | null => {
    try {
        return localStorage.getItem('access_token');
    } catch {
        return null;
    }
};

/**
 * Set authentication token in localStorage
 */
const setAuthToken = (token: string): void => {
    try {
        localStorage.setItem('access_token', token);
    } catch {
        // Unable to set token in localStorage
    }
};

/**
 * Build full URL from endpoint
 */
const buildUrl = (endpoint: string): string => {
    const baseUrl = config.api.baseUrl.replace(/\/$/, '');
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${baseUrl}${cleanEndpoint}`;
};

/**
 * Create abort controller with timeout
 */
const createAbortController = (
    timeout: number,
    externalSignal?: AbortSignal
): { controller: AbortController; timeoutId: ReturnType<typeof setTimeout> } => {
    const controller = new AbortController();

    const timeoutId = setTimeout(() => {
        controller.abort();
    }, timeout);

    if (externalSignal) {
        externalSignal.addEventListener('abort', () => {
            clearTimeout(timeoutId);
            controller.abort();
        });
    }

    return { controller, timeoutId };
};

/**
 * Parse response body
 */
const parseResponse = async <T>(response: Response): Promise<T> => {
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
        return response.json() as Promise<T>;
    }

    const text = await response.text();
    if (text === '') {
        return undefined as unknown as T;
    }
    return text as unknown as T;
};

/**
 * Low-level fetch helper — performs a single HTTP request without retry logic.
 */
const performFetch = async <TResponse, TBody = unknown>(
    endpoint: string,
    options: RequestOptions<TBody> = {}
): Promise<TResponse> => {
    const { method = 'GET', body, headers = {}, signal, includeAuth } = options;
    const url = buildUrl(endpoint);

    const { controller, timeoutId } = createAbortController(
        config.api.timeout,
        signal
    );

    const requestHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...headers,
    };

    const isAuthEndpoint = endpoint.includes('/auth/');
    const shouldIncludeAuth = includeAuth ?? !isAuthEndpoint;

    // Inject authorization token if available
    if (shouldIncludeAuth) {
        const token = getAuthToken();
        if (token) {
            requestHeaders['Authorization'] = `Bearer ${token}`;
        }
    }

    try {
        const response = await fetch(url, {
            method,
            headers: requestHeaders,
            body: body ? JSON.stringify(body) : undefined,
            signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            let apiError: ApiError | undefined;
            try {
                const errorResponse = await parseResponse<ApiResponse<unknown>>(response);
                apiError = errorResponse.error;
            } catch {
                // Unable to parse error response
            }
            throw new HttpError(response.status, response.statusText, apiError);
        }

        return parseResponse<TResponse>(response);
    } catch (error) {
        clearTimeout(timeoutId);

        if (error instanceof HttpError) {
            throw error;
        }

        if (error instanceof Error) {
            if (error.name === 'AbortError') {
                throw new TimeoutError();
            }
            throw new NetworkError(error.message);
        }

        throw new NetworkError();
    }
};

/**
 * HTTP client for making API requests
 */
export const httpClient = {
    /**
     * Make an HTTP request.
     * Automatically retries once after refreshing the access token if a 401 is received.
     * Dispatches an 'auth:logout' event if the refresh token is also invalid.
     */
    async request<TResponse, TBody = unknown>(
        endpoint: string,
        options: RequestOptions<TBody> = {}
    ): Promise<TResponse> {
        const method = options.method ?? 'GET';
        const isAuthEndpoint = endpoint.includes('/auth/');
        const shouldNotifySuccess =
            options.notifyOnSuccess ??
            (MUTATION_METHODS.includes(method) && !isAuthEndpoint);
        const shouldNotifyError = options.notifyOnError ?? !isAuthEndpoint;

        try {
            const response = await performFetch<TResponse, TBody>(endpoint, options);

            if (shouldNotifySuccess) {
                notifySuccess(
                    options.successTitle ?? getDefaultSuccessTitle(method, endpoint),
                    options.successMessage
                );
            }

            return response;
        } catch (error) {
            let errorAlreadyNotified = false;

            if (error instanceof HttpError && error.status === 401 && !isAuthEndpoint) {
                const refreshToken = localStorage.getItem('refresh_token');
                if (refreshToken) {
                    try {
                        const refreshResponse = await performFetch<{
                            access?: string;
                            access_token?: string;
                            refresh?: string;
                            refresh_token?: string;
                        }, { refresh: string }>(
                            '/auth/refresh/',
                            { method: 'POST', body: { refresh: refreshToken }, includeAuth: false }
                        );
                        const newAccessToken = refreshResponse.access ?? refreshResponse.access_token;
                        const newRefreshToken = refreshResponse.refresh ?? refreshResponse.refresh_token;

                        if (!newAccessToken) {
                            throw new HttpError(401, 'No access token returned from refresh');
                        }

                        setAuthToken(newAccessToken);
                        if (newRefreshToken) {
                            localStorage.setItem('refresh_token', newRefreshToken);
                        }
                        // Retry the original request with the new token
                        return await performFetch<TResponse, TBody>(endpoint, options);
                    } catch {
                        // Refresh failed — clear credentials and notify the app
                        localStorage.removeItem('access_token');
                        localStorage.removeItem('refresh_token');
                        localStorage.removeItem('user');
                        window.dispatchEvent(new Event('auth:logout'));
                        notifyError('Tu sesión ha expirado. Inicia sesión de nuevo.', 'Sesión expirada');
                        errorAlreadyNotified = true;
                    }
                }
            }

            if (shouldNotifyError && !errorAlreadyNotified) {
                notifyError(error, options.errorTitle ?? getDefaultErrorTitle(endpoint));
            }

            throw error;
        }
    },

    /**
     * GET request
     */
    get<TResponse>(
        endpoint: string,
        options?: Omit<RequestOptions, 'method' | 'body'>
    ): Promise<TResponse> {
        return this.request<TResponse>(endpoint, { ...options, method: 'GET' });
    },

    /**
     * POST request
     */
    post<TResponse, TBody = unknown>(
        endpoint: string,
        body: TBody,
        options?: Omit<RequestOptions, 'method' | 'body'>
    ): Promise<TResponse> {
        return this.request<TResponse, TBody>(endpoint, { ...options, method: 'POST', body });
    },

    /**
     * PUT request
     */
    put<TResponse, TBody = unknown>(
        endpoint: string,
        body: TBody,
        options?: Omit<RequestOptions, 'method' | 'body'>
    ): Promise<TResponse> {
        return this.request<TResponse, TBody>(endpoint, { ...options, method: 'PUT', body });
    },

    /**
     * PATCH request
     */
    patch<TResponse, TBody = unknown>(
        endpoint: string,
        body: TBody,
        options?: Omit<RequestOptions, 'method' | 'body'>
    ): Promise<TResponse> {
        return this.request<TResponse, TBody>(endpoint, { ...options, method: 'PATCH', body });
    },

    /**
     * DELETE request
     */
    delete<TResponse>(
        endpoint: string,
        options?: Omit<RequestOptions, 'method' | 'body'>
    ): Promise<TResponse> {
        return this.request<TResponse>(endpoint, { ...options, method: 'DELETE' });
    },
};

export default httpClient;

/**
 * Export authentication utilities for use by other services
 */
export { getAuthToken, setAuthToken };
